<script setup lang="ts">
import {
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipRoot,
  TooltipTrigger,
} from 'reka-ui';

type Props = {
  /** The hint shown on hover or keyboard focus. */
  text: string;
  /**
   * Renders the trigger on its own, with no hint attached. For triggers whose
   * hint only applies in one state, such as a card that explains itself only
   * while it is locked.
   */
  disabled?: boolean;
  /**
   * Widens the hint, left-aligns it and keeps the line breaks in `text`, for
   * explanations that run to several labelled lines rather than a short hint.
   */
  wide?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
};

const props = defineProps<Props>();

defineOptions({ inheritAttrs: false });
</script>

<template>
  <slot v-if="props.disabled" />
  <TooltipRoot v-else disable-hoverable-content>
    <TooltipTrigger as-child>
      <slot />
    </TooltipTrigger>
    <TooltipPortal>
      <TooltipContent
        :side="props.side"
        :side-offset="8"
        class="text-caption z-[10000] rounded-sm px-xl py-md bg-secondary text-on-secondary"
        :class="
          props.wide
            ? 'max-w-96 text-left whitespace-pre-line'
            : 'max-w-60 text-center'
        "
      >
        {{ props.text }}
        <TooltipArrow :width="16" :height="8" class="fill-secondary" />
      </TooltipContent>
    </TooltipPortal>
  </TooltipRoot>
</template>
